import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Player, PlayerGameStats } from '../../types';
import { Heading } from '../../ui/heading';
import { Text } from '../../ui/text';
import { EmptyState, StatCard } from '../../components/PageChrome';

export default function Stats() {
  const { teamId } = useParams<{ teamId: string }>();
  const [stats, setStats] = useState<PlayerGameStats[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!teamId) return;
    void (async () => {
      try {
        const [s, p] = await Promise.all([admin.listTeamStats(teamId), admin.listPlayers(teamId)]);
        setStats(s.stats);
        setPlayers(p.players);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [teamId]);

  const byPlayer = useMemo(() => {
    const map = new Map<string, PlayerGameStats>();
    for (const row of stats) {
      const existing = map.get(row.playerId);
      if (!existing) {
        map.set(row.playerId, structuredClone(row));
        continue;
      }
      existing.batting.pa += row.batting.pa;
      existing.batting.ab += row.batting.ab;
      existing.batting.h += row.batting.h;
      existing.batting.rbi += row.batting.rbi;
      existing.batting.bb += row.batting.bb;
      existing.batting.so += row.batting.so;
      existing.pitching.outs += row.pitching.outs;
      existing.pitching.pitches += row.pitching.pitches;
    }
    return [...map.values()].sort((a, b) => b.batting.pa - a.batting.pa);
  }, [stats]);

  const name = (playerId: string) => {
    const p = players.find((pl) => pl.playerId === playerId);
    return p ? `#${p.jerseyNumber} ${p.name}` : playerId;
  };

  return (
    <div className="space-y-6">
      <div>
        <Heading level={2}>Stats</Heading>
        <Text className="mt-1 text-sm">Derived batting and pitching totals from scorebook v2 events.</Text>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Stat rows" value={stats.length} />
        <StatCard label="Players" value={byPlayer.length} tone="emerald" />
        <StatCard label="Plate appearances" value={byPlayer.reduce((sum, s) => sum + s.batting.pa, 0)} />
      </div>
      {error ? <Text className="text-red-400">{error}</Text> : null}
      {byPlayer.length === 0 ? (
        <EmptyState title="No stats yet" body="Scorebook v2 events will populate this table." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-zinc-800">
          <table className="min-w-full divide-y divide-zinc-800 text-sm">
            <thead className="bg-zinc-900 text-left text-zinc-400">
              <tr>
                {['Player', 'PA', 'AB', 'H', 'RBI', 'BB', 'SO', 'Pitches', 'IP'].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 bg-zinc-950">
              {byPlayer.map((s) => (
                <tr key={s.playerId}>
                  <td className="px-3 py-2 font-medium text-zinc-100">{name(s.playerId)}</td>
                  <td className="px-3 py-2">{s.batting.pa}</td>
                  <td className="px-3 py-2">{s.batting.ab}</td>
                  <td className="px-3 py-2">{s.batting.h}</td>
                  <td className="px-3 py-2">{s.batting.rbi}</td>
                  <td className="px-3 py-2">{s.batting.bb}</td>
                  <td className="px-3 py-2">{s.batting.so}</td>
                  <td className="px-3 py-2">{s.pitching.pitches}</td>
                  <td className="px-3 py-2">{Math.floor(s.pitching.outs / 3)}.{s.pitching.outs % 3}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
